import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, Paper, Stack, TextField, Typography,
} from "@mui/material";

// FC265: historical figures are observations in imported rows, NOT verified sales velocity.
// Only the explicit Save Research action writes a research-only evidence revision.
const FIELDS = [
  ["sale_price", "Hypothetical order item price (AUD)"],
  ["buyer_postage", "Buyer-paid shipping (AUD)"],
  ["discount", "Discount (AUD)"],
  ["fee_rate_percent", "Platform fee rate (%)"],
  ["ad_rate_percent", "Promotion rate (%)"],
  ["fixed_fee", "Fixed fee (AUD)"],
  ["seller_postage", "Seller postage outlay (AUD)"],
  ["packing", "Packing/handling (AUD)"],
  ["cogs_per_card", "Assumed cost per card (AUD)"],
  ["cards_per_order", "Cards consumed in order"],
];
const blanks = () => Object.fromEntries(FIELDS.map(([key]) => [key, ""]));
const display = (value) => value === null || value === undefined || value === "" ? "Unknown" : String(value);
const currency = (v) => v === null || v === undefined ? "Unknown" : `A$${Number(v).toFixed(2)}`;
const formatDate = (x) => x ? String(x).replace("T", " ") : "Not recorded";

function Summary({ title, window }) {
  if (!window) return <Alert severity="warning">{title}: no safe exact eBay listing ID mapping available.</Alert>;
  return <Paper variant="outlined" sx={{ p: 2, minWidth: 0 }}>
    <Typography variant="subtitle2">{title} — observed imported orders</Typography>
    <Typography variant="caption" color="text.secondary">{window.coverage} · {window.source}</Typography>
    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>Coverage window: {formatDate(window.window_start_utc)} to {formatDate(window.window_end_utc)} · First/last observed: {formatDate(window.first_observed_sale)} / {formatDate(window.last_observed_sale)}</Typography>
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 1.5, my: 1.5 }}>
      <Box><Typography variant="caption">Order item rows</Typography><Typography>{display(window.eligible_rows)}</Typography></Box>
      <Box><Typography variant="caption">Observed units</Typography><Typography>{display(window.observed_units)}</Typography></Box>
      <Box><Typography variant="caption">Recorded item total</Typography><Typography>{currency(window.observed_item_total_aud)}</Typography></Box>
      <Box><Typography variant="caption">Recorded item profit</Typography><Typography>{currency(window.recorded_item_profit_aud)}</Typography></Box>
      <Box><Typography variant="caption">Allocated fees</Typography><Typography>{currency(window.recorded_fee_alloc_aud)}</Typography></Box>
      <Box><Typography variant="caption">Ad fees</Typography><Typography>{currency(window.recorded_ad_fee_aud)}</Typography></Box>
      <Box><Typography variant="caption">Ship allocation</Typography><Typography>{currency(window.recorded_ship_alloc_aud)}</Typography></Box>
      <Box><Typography variant="caption">Recorded COGS</Typography><Typography>{currency(window.recorded_cogs_aud)}</Typography></Box>
    </Box>
    <Typography variant="caption" color="text.secondary">{window.coverage_note}</Typography>
    {window.excluded_unverified_status_rows > 0 &&
      <Alert severity="warning" sx={{ mt: 1 }}>{window.excluded_unverified_status_rows} non-completed, unknown, cancelled or refund-marked rows excluded; partial refunds may remain unidentified.</Alert>}
  </Paper>;
}

export default function AIBusinessAnalysisDialog({ open, run, onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [assumptions, setAssumptions] = useState(blanks);
  const [status, setStatus] = useState("NEEDS_REVIEW");
  const [notes, setNotes] = useState("");
  const [viewRevision, setViewRevision] = useState(null);
  const active = run?.status === "IN_PROGRESS";
  const url = run?.id ? `/api/ai-operator/research-runs/${run.id}/cammez-business-analysis` : null;
  const refresh = useCallback(async () => {
    if (!url) return;
    setLoading(true); setError("");
    try {
      const response = await axios.get(url);
      if (response.data?.ok !== true || !response.data.research) throw new Error("Unexpected analysis response");
      setData(response.data.research);
      setViewRevision(null);
      const saved = response.data.research.latest?.evidence_json;
      setAssumptions(saved?.assumptions ? Object.fromEntries(FIELDS.map(([k]) =>
        [k, saved.assumptions[k] === null || saved.assumptions[k] === undefined ? "" : String(saved.assumptions[k])])) : blanks());
      setStatus(saved?.evidence_status || "NEEDS_REVIEW");
      setNotes(saved?.notes || "");
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Business Analysis request failed");
    } finally { setLoading(false); }
  }, [url]);
  useEffect(() => { if (open) refresh(); else { setData(null); setError(""); setNotice(""); } }, [open, refresh]);
  const save = async () => {
    if (!active || !url) return;
    setSaving(true); setError(""); setNotice("");
    try {
      const values = Object.fromEntries(FIELDS.map(([key]) => [key, assumptions[key] === "" ? null :
        key === "cards_per_order" ? Number(assumptions[key]) : assumptions[key]]));
      // The server recalculates from verified sources; browser never supplies figures or source rows.
      const response = await axios.post(url, { listing_id: Number(run.listing_id),
        assumptions: values, evidence_status: status, research_notes: notes });
      if (response.data?.ok !== true) throw new Error("Research save was not confirmed");
      setNotice("Research-only evidence revision saved. No business or listing data changed.");
      await refresh();
      onSaved?.();
    } catch (err) { setError(err?.response?.data?.error || err?.message || "Could not save research"); }
    finally { setSaving(false); }
  };
  const current = data?.current;
  const latest = viewRevision || data?.latest;
  const evidence = latest?.evidence_json;
  return <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="lg" scroll="paper">
    <DialogTitle>Cammez Business Analysis — Listing #{run?.listing_id}</DialogTitle>
    <DialogContent dividers>
      <Stack gap={2}>
        <Alert severity="info">Internal Cammez data only. No live eBay sync, customer records, AI recommendation or listing changes. Historical sales are limited to observed imported rows, not a complete audited sales history.</Alert>
        <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
          <Chip label={`Run #${run?.id}`} variant="outlined" />
          <Chip label={active ? "Open research" : "Closed — view only"} variant="outlined" />
          <Button variant="outlined" size="small" disabled={loading || saving} onClick={refresh}>Load / Refresh Data</Button>
          {loading && <CircularProgress size={18} />}
        </Stack>
        {error && <Alert severity="error">{error}</Alert>}
        {notice && <Alert severity="success">{notice}</Alert>}
        {current && <>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={700}>Current stored listing — fact</Typography>
            <Typography>Local #{display(current.listing?.id)} · {display(current.listing?.card_name)} · SKU {display(current.listing?.sku)}</Typography>
            <Typography variant="body2">Price {currency(current.listing?.listing_price)} · Stored available qty {display(current.listing?.ebay_available_quantity)} · Finish {display(current.listing?.fc256_verified_finish)}</Typography>
            <Typography variant="caption" color="text.secondary">Retrieved: {formatDate(current.retrieved_at)} · Listing join: {display(current.join)} · {display(current.reason)}</Typography>
          </Paper>
          {current.stale_fields?.length > 0 && <Alert severity="warning">Research-run listing snapshot differs from stored values: {current.stale_fields.join(", ")}. Review identity before relying on the analysis.</Alert>}
          {current.join !== "EXACT_EBAY_LISTING_ID" && <Alert severity="warning">No uniquely attributable sales history: {current.reason}. A SKU/title match is not used.</Alert>}
          <Summary title="Last 30 days" window={current.windows?.["30"]} />
          <Summary title="Last 90 days" window={current.windows?.["90"]} />
          <Alert severity="warning">Source coverage: imported sales completeness Unknown; refunds Unknown; saleable stock quantity Unknown; per-card COGS Unknown. Recorded item profit already includes its recorded deductions—do not subtract displayed fees a second time.</Alert>
        </>}
        {evidence && <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight={700}>Saved research evidence (revision #{latest?.revision_no})</Typography>
          <Typography variant="caption" color="text.secondary">Captured: {formatDate(latest?.created_at)} · status: {display(evidence.evidence_status)} · model: {display(evidence.calculation_version)}</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>{evidence.notes || "No notes"}</Typography>
          <Typography variant="body2">Estimated order contribution: {currency(evidence.estimate?.contribution_aud)} · Per card: {currency(evidence.estimate?.contribution_per_card_aud)}</Typography>
          <Typography variant="caption" color="text.secondary">{evidence.estimate?.formula}</Typography>
          {evidence.estimate?.missing?.length > 0 && <Typography variant="body2" color="text.secondary">Estimate unavailable until explicitly provided: {evidence.estimate.missing.join(", ")}</Typography>}
          {evidence.estimate?.breakdown_aud && <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 1, mt: 1 }}>
            {Object.entries(evidence.estimate.breakdown_aud).map(([k, v]) => <Box key={k}><Typography variant="caption">{k.replaceAll("_", " ")}</Typography><Typography>{currency(v)}</Typography></Box>)}
          </Box>}
        </Paper>}
        {!!data?.revisions?.length && <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1}>
          <Typography variant="body2">Research history:</Typography>
          {data.revisions.map((r) => <Button key={r.id} size="small" variant={latest?.id === r.id ? "contained" : "outlined"} onClick={() => setViewRevision(r)}>v{r.revision_no}</Button>)}
          <Button size="small" onClick={() => setViewRevision(null)}>Latest</Button>
        </Stack>}
        {active && <>
          <Divider />
          <Typography variant="subtitle1" fontWeight={700}>Edit assumptions — research only</Typography>
          <Typography variant="body2" color="text.secondary">No rates, postage or COGS are prefilled as facts. Supply an explicit order scenario; missing fields remain Unknown. Rates apply to item + buyer postage − discounts. Save makes a new research evidence revision, not a business transaction.</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 2 }}>
            {FIELDS.map(([key, label]) => <TextField key={key} label={label} size="small" type="number"
              inputProps={{ min: key === "cards_per_order" ? 1 : 0, step: key === "cards_per_order" ? 1 : ".01" }}
              value={assumptions[key]} onChange={(e) => setAssumptions((old) => ({ ...old, [key]: e.target.value }))} />)}
          </Box>
          <TextField multiline minRows={2} label="Research notes / evidence limitations" value={notes}
            onChange={(e) => setNotes(e.target.value)} inputProps={{ maxLength: 2000 }} />
          <TextField select SelectProps={{ native: true }} label="Research-only stage status" value={status}
            onChange={(e) => setStatus(e.target.value)}>
            <option value="IN_PROGRESS">In progress</option>
            <option value="NEEDS_REVIEW">Needs review — source coverage incomplete</option>
            <option value="INSUFFICIENT_EVIDENCE">Insufficient evidence</option>
          </TextField>
        </>}
      </Stack>
    </DialogContent>
    <DialogActions><Button onClick={onClose} disabled={saving}>Close</Button>
      {active && <Button onClick={save} variant="contained" disabled={loading || saving || !data?.current?.listing}>
        {saving ? "Saving…" : "Save Research (no listing changes)"}</Button>}
    </DialogActions>
  </Dialog>;
}
